import {APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, Injector, NgModule} from '@angular/core';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';

import {AppComponent} from './app.component';

import {ExportAsModule} from 'ngx-export-as';
import {CookieService} from 'ngx-cookie-service';

// routing
import {ActivatedRouteSnapshot, RouteReuseStrategy, RouterStateSnapshot} from '@angular/router';
import {CustomRouteReuseStrategy} from './routing/custom-route-reuse-strategy';
import {AppRoutingModule} from './routing/app-routing.module';

// service
import {ConfigServiceProvider} from './services/config/config.service.provider';
import {TokenInterceptor} from './services/auth/token.interceptor';
import {HttpErrorInterceptor} from './services/interceptor/http-error.interceptor';
import {IsNotLoggedPageGuard} from './services/auth/is-not-logged.guard';
import {IsLoggedPageGuard} from './services/auth/is-logged.guard';

// translate
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';

// primeng
import {TableModule} from 'primeng/table';
import {MenuModule} from 'primeng/menu';
import {SlideMenuModule} from 'primeng/slidemenu';
import {TieredMenuModule} from 'primeng/tieredmenu';
import {MenubarModule} from 'primeng/menubar';
import {ToastModule} from 'primeng/toast';
import {SidebarModule} from 'primeng/sidebar';
import {TooltipModule} from 'primeng/tooltip';
import {DropdownModule} from 'primeng/dropdown';
import {MultiSelectModule} from 'primeng/multiselect';
import {ListboxModule} from 'primeng/listbox';
import {AutoCompleteModule} from 'primeng/autocomplete';
import {DialogModule} from 'primeng/dialog';
import {CheckboxModule} from 'primeng/checkbox';
import {ToggleButtonModule} from 'primeng/togglebutton';
import {InputSwitchModule} from 'primeng/inputswitch';
import {InputTextModule} from 'primeng/inputtext';
import {InputNumberModule} from 'primeng/inputnumber';
import {PasswordModule} from 'primeng/password';
import {RadioButtonModule} from 'primeng/radiobutton';
import {SelectButtonModule} from 'primeng/selectbutton';
import {MessagesModule} from 'primeng/messages';
import {MessageModule} from 'primeng/message';
import {ButtonModule} from 'primeng/button';
import {ToolbarModule} from 'primeng/toolbar';
import {ConfirmationService, MessageService} from 'primeng/api';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ConfirmPopupModule} from 'primeng/confirmpopup';
import {CardModule} from 'primeng/card';
import {FieldsetModule} from 'primeng/fieldset';
import {DividerModule} from 'primeng/divider';
import {InputTextareaModule} from 'primeng/inputtextarea';
import {TabViewModule} from 'primeng/tabview';
import {ProgressBarModule} from 'primeng/progressbar';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {SplitterModule} from 'primeng/splitter';
import {PanelModule} from 'primeng/panel';
import {TabMenuModule} from 'primeng/tabmenu';
import {TreeTableModule} from 'primeng/treetable';
import {ChipsModule} from 'primeng/chips';
import {CalendarModule} from 'primeng/calendar';
import {OverlayPanelModule} from 'primeng/overlaypanel';
import {BadgeModule} from 'primeng/badge';


// material
import {MatTabsModule} from '@angular/material/tabs';
import {MatIconModule} from '@angular/material/icon';

// crystal
import {SideBarComponent} from './crystal/layout/sidebar/side-bar.component';
import {MainLayoutComponent} from './crystal/layout/main/main-layout.component';
import {HeaderMenuComponent} from './crystal/header/menu/header-menu.component';
import {HeaderLogoComponent} from './crystal/header/logo/header-logo.component';
import {
  FooterWithEnvironmentInformationsComponent
} from './crystal/footer-with-environment-informations/footer-with-environment-informations.component';
import {LoginComponent} from './crystal/login/login.component';
import {CcDetailDialogComponent} from "./crystal-elements/detail-dialog/cc-detail-dialog.component";
import {CcDetailComponent} from "./crystal-elements/detail-dialog/detail/cc-detail.component";
import {ReadonlyTableComponent} from "./crystal-elements/readonly-table/readonly-table.component";

// crystal-element
import {LanguageComponent} from './crystal-elements/rec/language/language.component';
import {LoginFormComponent} from './crystal-elements/login-form/login-form.component';
import {LoginCredentialsComponent} from './crystal-elements/login-form/login-credentials/login-credentials.component';
import {ForgotPasswordComponent} from './crystal-elements/login-form/forgot-password/forgot-password.component';
import {ChangePasswordComponent} from './crystal-elements/login-form/change-password/change-password.component';
import {ResetPasswordComponent} from './crystal-elements/login-form/reset-password/reset-password.component';
import {ExpiredTokenComponent} from './crystal-elements/login-form/expired-token/expired-token.component';
import {InputRegularComponent} from './crystal-elements/rec/input/regular/input-regular.component';
import {ButtonComponent} from './crystal-elements/rec/button/button/button.component';
import {CheckboxComponent} from './crystal-elements/rec/checkbox/checkbox.component';
import {DialogComponent} from './crystal-elements/rec/dialog/dialog.component';
import {RadioFieldComponent} from './crystal-elements/rec/form-component/radio-field/radio-field.component';
import {ValidationComponent} from './crystal-elements/rec/form-component/validation/validation.component';
import {LogoComponent} from './crystal-elements/logo/logo.component';
import {LightLogoComponent} from './crystal-elements/logo/light-logo.component';
import {CustomerLogoComponent} from "./crystal-elements/logo/customer-logo.component";
import {CrudBaseComponent} from './crystal-elements/crud/crud-base.component';
import {RichFieldComponent} from './crystal-elements/form-dialog/form/rich-field/rich-field.component';
import {
  SingleRichFieldComponent
} from './crystal-elements/form-dialog/form/single-rich-field/single-rich-field.component';
import {MapFieldComponent} from './crystal-elements/form-dialog/form/map-field/map-field.component';
import {
  ReadonlyMapFieldComponent
} from "./crystal-elements/detail-dialog/detail/readonly-map-field/readonly-map-field.component";
import {
  ReadonlyRichFieldComponent
} from "./crystal-elements/detail-dialog/detail/readonly-rich-field/readonly-rich-field.component";
import {CronFieldComponent} from './crystal-elements/form-dialog/form/cron-field/cron-field.component';
import {CcFormComponent} from './crystal-elements/form-dialog/form/cc-form.component';
import {CcFormDialogComponent} from './crystal-elements/form-dialog/cc-form-dialog.component';
import {CcTableComponent} from './crystal-elements/table/cc-table.component';
import {CcTreeTableComponent} from './crystal-elements/tree-table/cc-tree-table.component';
import {CrudTableComponent} from './crystal-elements/crud/crud-table.component';
import {CcVscrollTableComponent} from './crystal-elements/cc-vscroll-table/cc-vscroll-table.component';
import {CctableBaseComponent} from './crystal-elements/table/cc-table-base.component';

// crystal-clear
import {TradeTableComponent} from './crystal-clear/clearing/table/trade/trade-table.component';
import {
  SingleAllocationFormComponent
} from './crystal-clear/clearing/table/form/allocation/single-allocation-form/single-allocation-form.component';
import {
  UpdateSingleAllocationFormComponent
} from './crystal-clear/clearing/table/form/allocation/update-single-allocation-form/update-single-allocation-form.component';
import {ClearingSidebarComponent} from './crystal-clear/clearing/clearing-sidebar/clearing-sidebar.component';
import {OrderTableComponent} from './crystal-clear/clearing/table/order/order-table.component';
import {OrderPofComponent} from './crystal-clear/order-pof/order-pof.component';
import {ClearingComponent} from './crystal-clear/clearing/clearing.component';
import {ClearingViewsComponent} from './crystal-clear/clearing/views/clearing-views.component';
import {AllocationTasksComponent} from './crystal-clear/clearing/allocation-tasks/allocation-tasks.component';
import {
  AllocationTasksTableComponent
} from './crystal-clear/clearing/allocation-tasks/tasks-table/allocation-tasks-table.component';
import {
  AllocationTasksButtonComponent
} from './crystal-clear/clearing/allocation-tasks/tasks-button/allocation-tasks-button.component';
import {CustomerSidebarComponent} from './crystal-clear/customer/customer-sidebar/customer-sidebar.component';
import {OwnerComponent} from './crystal-clear/customer/owner/owner.component';
import {CustomerComponent} from './crystal-clear/customer/customer/customer.component';
import {AccountComponent} from './crystal-clear/customer/account/account.component';
import {AcceptanceRuleComponent} from './crystal-clear/customer/acceptance-rule/acceptance-rule.component';
import {CustomerLimitsComponent} from './crystal-clear/customer/customer-limits/customer-limits.component';
import {BrokerComponent} from './crystal-clear/customer/broker/broker.component';
import {BankAccountComponent} from './crystal-clear/customer/bank-account/bank-account.component';
import {AccountTraderComponent} from './crystal-clear/customer/account-trader/account-trader.component';
import {TraderComponent} from './crystal-clear/customer/trader/trader.component';
import {VatRateComponent} from './crystal-clear/customer/vat-rate/vat-rate.component';
import {MoneyOwnerComponent} from './crystal-clear/customer/money-owner/money-owner.component';
import {BrokerageTableComponent} from './crystal-clear/customer/brokerage-table/brokerage-table.component';
import {ArchiveSidebarComponent} from './crystal-clear/archive/archive-sidebar/archive-sidebar.component';
import {RecapSidebarComponent} from './crystal-clear/archive/recap/recap-sidebar/recap-sidebar.component';
import {ReportFormComponent} from './crystal-clear/archive/form/report-form/report-form.component';
import {ReportProfileComponent} from './crystal-clear/archive/profile/report-profile/report-profile.component';
import {RecapProfileComponent} from './crystal-clear/archive/recap/recap-profile/recap-profile.component';
import {RecapListComponent} from './crystal-clear/archive/recap/recap-list-dialog/recap-list/recap-list.component';
import {RecapListDialogComponent} from './crystal-clear/archive/recap/recap-list-dialog/recap-list-dialog.component';
import {RecapTasksComponent} from './crystal-clear/archive/recap/recap-tasks/recap-tasks.component';
import {ReportTasksComponent} from './crystal-clear/archive/report-tasks/report-tasks.component';
import {ReportTasksTableComponent} from './crystal-clear/archive/report-tasks/tasks-table/report-tasks-table.component';
import {
  ReportTasksButtonComponent
} from './crystal-clear/archive/report-tasks/tasks-button/report-tasks-button.component';
import {UserComponent} from './crystal-clear/security/user/user.component';
import {UserFormComponent} from './crystal-clear/security/user-form/user-form.component';
import {UserViewComponent} from './crystal-clear/internal/user-view/user-view.component';
import {UserViewFormComponent} from './crystal-clear/internal/user-view-form/user-view-form.component';
import {UserViewDialogComponent} from './crystal-clear/internal/user-views-dialog/user-view-dialog.component';
import {SchedulerViewComponent} from './crystal-clear/tools/scheduler-view/scheduler-view.component';
import {SyncComponent} from './crystal-clear/support/sync/sync.component';
import {HeaderMenuLiteComponent} from "./crystal/header/menu/header-menu-lite.component";
import {DockModule} from "primeng/dock";
import {DockComponent} from "./crystal/layout/dashboard/dock-layout.component";
import {RippleModule} from "primeng/ripple";
import {VirtualScrollerModule} from "primeng/virtualscroller";
import {SkeletonModule} from "primeng/skeleton";
import {SafeHtmlPipe} from "./utils/safe-html.pipe";
import {StyleStringToObjPipe} from "./utils/style-string-to-obj.pipe";
import {NgxEchartsModule} from "ngx-echarts";
import {ChartModule} from "primeng/chart";
import {SummaryChartComponent} from "./crystal/charts/summary-chart.component";
import {DashboardComponent} from './crystal/layout/dashboard/dashboard-layout.component';
import {CardComponent} from './crystal/layout/dashboard/card.component';
import {MatGridListModule} from '@angular/material/grid-list';
import {MatCardModule} from '@angular/material/card';
import {MatMenuModule} from '@angular/material/menu';
import {MatButtonModule} from '@angular/material/button';
import {ChartExchangeButtonsComponent} from "./crystal/charts/chart-exchange-buttons-component";
import {DataTrendChartComponent} from "./crystal/charts/data-trend-chart.component";
import {ClearedUnclearedChartComponent} from "./crystal/charts/cleared-uncleared-chart.component";
import {
  SplitAllocationFormComponent
} from "./crystal-clear/clearing/table/form/allocation/split-allocation-form/split-allocation-form.component";
import {AddDeleteFieldComponent} from "./crystal-elements/form-dialog/form/add-delete-field/add-delete-field.component";
import {CcSubFormBaseComponent} from "./crystal-elements/form-dialog/form/cc-sub-form-base.component";
import {
  AllocationFormBaseComponent
} from "./crystal-clear/clearing/table/form/allocation/allocation-form-base.component";
import {
  SingleAllocationFormBaseComponent
} from "./crystal-clear/clearing/table/form/allocation/single-allocation-form-base.component";
import {OrderDetailComponent} from "./crystal-clear/clearing/table/order/order-detail/order-detail.component";
import {
  SingleAllocationMultiExchangeFormComponent
} from "./crystal-clear/clearing/table/form/allocation/single-allocation-multi-exchange-form/single-allocation-multi-exchange-form.component";
import {
  UpdateSplitAllocationFormComponent
} from "./crystal-clear/clearing/table/form/allocation/update-split-allocation-form/update-split-allocation-form.component";
import {
  SplitAllocationFormBaseComponent
} from "./crystal-clear/clearing/table/form/allocation/split-allocation-form-base.component";
// CDK
import {LayoutModule, MediaMatcher} from '@angular/cdk/layout';
import {ScrollingModule} from "@angular/cdk/scrolling";
import {ClipboardModule} from "@angular/cdk/clipboard";
import {
  ExchangeApsOverrideFormComponent
} from "./crystal-clear/clearing/table/form/exchange-aps-override-form/exchange-aps-override-form.component";
import {TableFormFieldComponent} from "./crystal-elements/form-dialog/form/table-form-field/table-form-field.component";
import {
  CcFieldDispatcherComponent
} from "./crystal-elements/form-dialog/form/field-dispatcher/cc-field-dispatcher.component";
import {DatePipe, DecimalPipe} from "@angular/common";
import {
  OverrideCustomReferenceFormComponent
} from "./crystal-clear/clearing/table/form/override-custom-reference-form/override-custom-reference-form.component";
import {MoveOrderFormComponent} from "./crystal-clear/clearing/table/form/move-order-form/move-order-form.component";
import {
  SelectTableColumnsFormComponent
} from "./crystal-clear/clearing/table/form/select-table-columns-form/select-table-columns-form.component";
import {
  PlResidualTableComponent
} from "./crystal-clear/clearing/pl-residual/pl-residual-table/pl-residual-table.component";
import {
  PlResidualTableButtonComponent
} from "./crystal-clear/clearing/pl-residual/pl-residual-table-button/pl-residual-table-button-component";
import {
  ReportTotalResidualFormComponent
} from "./crystal-clear/archive/report-total-residual/report-total-residual-adjust-form/report-total-residual-form.component";
import {
  ReportTotalResidualAdjustButtonComponent
} from "./crystal-clear/archive/report-total-residual/report-total-residual-adjust-button/report-total-residual-adjust-button.component";
import {ClearingHouseComponent} from "./crystal-clear/exchange/clearing-house/clearing-house.component";
import {ExchangeSidebarComponent} from "./crystal-clear/exchange/exchange-sidebar/exchange-sidebar.component";
import {ExchangeDetailComponent} from "./crystal-clear/exchange/exchange/exchange-detail.component";
import {AccountTypeComponent} from "./crystal-clear/exchange/account-type/account-type.component";
import {TradingEnvironmentComponent} from "./crystal-clear/exchange/trading-environment/trading-environment.component";
import {ContractComponent} from "./crystal-clear/exchange/contract/contract.component";
import {
  MaturityRejectionRuleComponent
} from "./crystal-clear/exchange/maturity-rejection-rule/maturity-rejection-rule.component";
import {OwnerCodeComponent} from "./crystal-clear/exchange/owner-code/owner-code.component";
import {BrokerCodeComponent} from "./crystal-clear/exchange/broker-code/broker-code.component";
import {
  OrderReferencePatternComponent
} from "./crystal-clear/mapping/order-reference-pattern/order-reference-pattern.component";
import {SourceComponent} from "./crystal-clear/mapping/source/source.component";
import {FeeComponent} from "./crystal-clear/mapping/fee/fee.component";
import {SrcMappingFeeComponent} from "./crystal-clear/mapping/src-mapping-fee/src-mapping-fee.component";
import {
  SrcMappingTradeTypeComponent
} from "./crystal-clear/mapping/src-mapping-trade-type/src-mapping-trade-type.component";
import {SrcMappingExchangeComponent} from "./crystal-clear/mapping/src-mapping-exchange/src-mapping-exchange.component";
import {
  SrcMappingAccountCodeComponent
} from "./crystal-clear/mapping/src-mapping-account-code/src-mapping-account-code.component";
import {SrcMappingTraderComponent} from "./crystal-clear/mapping/src-mapping-trader/src-mapping-trader.component";
import {
  SrcMappingBrokerCodeComponent
} from "./crystal-clear/mapping/src-mapping-broker-code/src-mapping-broker-code.component";
import {SrcMappingContractComponent} from "./crystal-clear/mapping/src-mapping-contract/src-mapping-contract.component";
import {MappingSidebarComponent} from "./crystal-clear/mapping/mapping-sidebar/mapping-sidebar.component";
import {
  TradeSelectionTableComponent
} from "./crystal-clear/clearing/table/trade/selection/selection-table/trade-selection-table.component";
import {TradeDetailComponent} from "./crystal-clear/clearing/table/trade/trade-detail/trade-detail.component";
import {RecapFormComponent} from "./crystal-clear/archive/recap/form/recap-form.component";
import {ArchiveFormAbsComponent} from "./crystal-clear/archive/form/archive-form-abs.component";
import {
  TradeSelectionTableDialogComponent
} from "./crystal-clear/clearing/table/trade/selection/selection-table-dialog/trade-selection-table-dialog.component";
import {SpinnerModule} from "primeng/spinner";
import {ProfileAbsComponent} from "./crystal-clear/archive/profile/profile-abs.component";
import {HelpComponent} from "./crystal/help/help.component";
import {SelectionSummaryComponent} from "./crystal-clear/clearing/table/selection-summary/selection-summary.component";
import {FindOptionTextPipe} from "./pipe/clearing/find-option-text-pipe.component";
import {
  TradeAggregateTableComponent
} from "./crystal-clear/clearing/table/trade-aggregate/trade-aggregate-table.component";
import {TradeTableBaseComponent} from "./crystal-clear/clearing/table/trade/trade-table-base.component";
import {
  OrderClearingTableComponent
} from "./crystal-clear/clearing/table/order-clearing/order-clearing-table.component";
import {
  ClearingDetailComponent
} from "./crystal-clear/clearing/table/order-clearing/clearing-detail/clearing-detail.component";
import {OrderTableBaseComponent} from "./crystal-clear/clearing/table/order/order-table-base.component";
import {CcSubDetailBaseComponent} from "./crystal-elements/detail-dialog/detail/cc-sub-detail-base.component";
import {HealthComponent} from "./crystal/header/health/health.component";
import {NotificationComponent} from "./crystal/header/notification/notification.component";
import {UserGroupComponent} from "./crystal-clear/security/user-group/user-group.component";
import {NotifyComponent} from "./crystal-clear/tools/notify/notify.component";
import {DateFormatPipe} from "./utils/date-format.pipe";
import {EditorModule} from "primeng/editor";
import {
  BatchUpdateProfileFormComponent
} from "./crystal-clear/archive/profile/batch-update-profile-form/batch-update-profile-form.component";
import {
  BatchUpdateUserFormComponent
} from "./crystal-clear/security/user/batch-update-user-form/batch-update-user-form.component";
import {ApplicationInitializerFactory} from "./translation.config";
import {AppConfigService} from "./services/app-config.service";
import {LastActiveService} from "./services/security/LastActiveService";
import {UserInfoStorageService} from "./services/auth/user-info-storage.service";
import {HeaderMenuBaseComponent} from "./crystal/header/menu/header-menu-base.component";
import {ContextMenuModule} from "./crystal-elements/cc-vscroll-table/contextmenu";
import {HomeLayoutComponent} from "./crystal/layout/home/home-layout.component";
import {QuartzCronModule} from "@sbzen/ng-cron";
import {OrderListModule} from "primeng/orderlist";
import {PickListModule} from "primeng/picklist";
import {ExecTraderComponent} from "./crystal-clear/customer/exec-trader/exec-trader.component";
import {BillingSidebarComponent} from "./crystal-clear/billing/billing-sidebar/billing-sidebar.component";
import {BillingListComponent} from "./crystal-clear/billing/list/billing-list.component";
import {InvoiceListTableComponent} from "./crystal-clear/billing/list/invoice-list-table/invoice-list-table.component";
import {
  InvoiceDetailTableComponent
} from "./crystal-clear/billing/detail/invoice-detail-table/invoice-detail-table.component";
import {
  InvoiceDetailDialogComponent
} from "./crystal-clear/billing/detail/invoice-detail-dialog/invoice-detail-dialog.component";
import {CcTableEditComponent} from "./crystal-elements/table-edit/cc-table-edit.component";
import {CcCellDispatcherComponent} from "./crystal-elements/table/cell-dispatcher/cc-cell-dispatcher.component";
import {NewETicketComponent} from "./crystal-clear/e-ticket/new-e-ticket/new-e-ticket.component";
import {ListETicketComponent} from "./crystal-clear/e-ticket/list-e-ticket/list-e-ticket.component";
import {ETicketViewsComponent} from "./crystal-clear/e-ticket/views/e-ticket-views.component";
import {InvoiceTasksComponent} from "./crystal-clear/billing/invoice-tasks/invoice-tasks.component";
import {
  OrderAmendmentTableComponent
} from "./crystal-clear/data-amendment/order-amendment/table/order-amendment-table.component";
import {
  OrderAmendmentDialogComponent
} from "./crystal-clear/data-amendment/order-amendment/dialog/order-amendment-dialog.component";
import {
  UpdateOutstandingFormComponent
} from "./crystal-clear/billing/list/invoice-list-table/update-outstanding-form/update-outstanding-form.component";
import {
  BatchUpdateOrderAmendmentFormComponent
} from "./crystal-clear/data-amendment/order-amendment/table/batch-update-form/batch-update-order-amendment-form.component";
import {FileUploadModule} from "primeng/fileupload";
import {
  FlexEditFormDialogComponent
} from "./crystal-clear/clearing/flex/flex-edit-form-dialog/flex-edit-form-dialog.component";
import {
  FlexAddFormDialogComponent
} from "./crystal-clear/clearing/flex/flex-add-form-dialog/flex-add-form-dialog.component";
import {
  CreateOrderFormComponent
} from "./crystal-clear/clearing/table/form/allocation/create-order-form/create-order-form.component";
import {
  FlexBatchEditFormDialogComponent
} from "./crystal-clear/clearing/flex/flex-batch-edit-form-dialog/flex-batch-edit-form-dialog.component";
import {FlexFormDialogBaseComponent} from "./crystal-clear/clearing/flex/flex-form-dialog-base.component";
import {
  FlexAddOrdersFormDialogComponent
} from "./crystal-clear/clearing/flex/flex-add-orders-form-dialog/flex-add-orders-form-dialog.component";
import {ClearingTableBaseWsComponent} from "./crystal-clear/clearing/table/clearing-table-base-ws.component";
import {ClearingTableBaseWsAbsComponent} from "./crystal-clear/clearing/table/clearing-table-base-ws-abs.component";
import {ForceFocusDirective} from "./crystal-elements/form-dialog/form/directive/force-focus-directive";
import {HogoButtonComponent} from "./crystal-clear/user-tools/hogo/hogo-button/hogo-button/hogo-button.component";
import {ParallaxBackgroundComponent} from "./crystal-elements/parallax-background/parallax-background.component";
import {
  UpdateTradePriceFormComponent
} from "./crystal-clear/clearing/table/form/update-trade-price-form/update-trade-price-form.component";
import {
  BatchActionCustomerFormComponent
} from "./crystal-clear/customer/customer/batch-update-form/batch-action-customer-form.component";
import {
  BatchActionAccountFormComponent
} from "./crystal-clear/customer/account/batch-update-form/batch-action-account-form.component";
import {
  BatchActionAccountTraderFormComponent
} from "./crystal-clear/customer/account-trader/batch-update-form/batch-action-account-trader-form.component";
import {TradeCrudComponent} from "./crystal-clear/support/trade-crud/trade-crud.component";
import {
  BatchActionSupportTradeFormComponent
} from "./crystal-clear/support/trade-crud/batch-update-form/batch-action-support-trade-form.component";
import {SupportSidebarComponent} from "./crystal-clear/support/support-sidebar/support-sidebar.component";
import {OrderCrudComponent} from "./crystal-clear/support/order-crud/order-crud.component";
import {
  BatchActionSupportOrderFormComponent
} from "./crystal-clear/support/order-crud/batch-update-form/batch-action-support-order-form.component";
import {ClearingCrudComponent} from "./crystal-clear/support/clearing-crud/clearing-crud.component";
import {
  BatchActionSupportClearingFormComponent
} from "./crystal-clear/support/clearing-crud/batch-update-form/batch-action-support-clearing-form.component";
import {TableWsBaseComponent} from "./services/websocket/table-ws-base.component";
import {CcVscrollTableBaseComponent} from "./crystal-elements/cc-vscroll-table/cc-vscroll-table-base.component";
import { CspMediaMatcher } from "./utils/csp-media-matcher";

@NgModule({
    declarations: [
      AppComponent,
      // crystal
      SideBarComponent,
      CcDetailDialogComponent,
      CcDetailComponent,
      ReadonlyTableComponent,
      MainLayoutComponent,
      HeaderMenuBaseComponent,
      HeaderMenuComponent,
      HeaderMenuLiteComponent,
      NotificationComponent,
      HeaderLogoComponent,
      FooterWithEnvironmentInformationsComponent,
      DockComponent,
      DashboardComponent,
      CardComponent,
      HomeLayoutComponent,
      // crystal-clear
      OrderTableBaseComponent,
      TradeTableBaseComponent,
      TradeTableComponent,
      TradeAggregateTableComponent,
      OrderClearingTableComponent,
      FlexFormDialogBaseComponent,
      FlexAddOrdersFormDialogComponent,
      FlexAddFormDialogComponent,
      FlexEditFormDialogComponent,
      FlexBatchEditFormDialogComponent,
      SingleAllocationFormComponent,
      SingleAllocationMultiExchangeFormComponent,
      SplitAllocationFormComponent,
      CreateOrderFormComponent,
      UpdateSplitAllocationFormComponent,
      UpdateSingleAllocationFormComponent,
      AllocationFormBaseComponent,
      SingleAllocationFormBaseComponent,
      SplitAllocationFormBaseComponent,
      ExchangeApsOverrideFormComponent,
      MoveOrderFormComponent,
      SelectTableColumnsFormComponent,
      OverrideCustomReferenceFormComponent,
      UpdateTradePriceFormComponent,
      OrderDetailComponent,
      TradeDetailComponent,
      ClearingDetailComponent,
      ClearingSidebarComponent,
      OrderTableComponent,
      ClearingTableBaseWsComponent,
      ClearingTableBaseWsAbsComponent,
      TableWsBaseComponent,
      OrderPofComponent,
      ClearingComponent,
      ClearingViewsComponent,
      AllocationTasksComponent,
      AllocationTasksTableComponent,
      AllocationTasksButtonComponent,
      CustomerSidebarComponent,
      OwnerComponent,
      CustomerComponent,
      BatchActionCustomerFormComponent,
      ExecTraderComponent,
      AccountComponent,
      BatchActionAccountFormComponent,
      AcceptanceRuleComponent,
      CustomerLimitsComponent,
      BrokerComponent,
      BankAccountComponent,
      AccountTraderComponent,
      BatchActionAccountTraderFormComponent,
      TraderComponent,
      VatRateComponent,
      MoneyOwnerComponent,
      BrokerageTableComponent,
      ExchangeSidebarComponent,
      ClearingHouseComponent,
      ExchangeDetailComponent,
      AccountTypeComponent,
      TradingEnvironmentComponent,
      ContractComponent,
      MaturityRejectionRuleComponent,
      OwnerCodeComponent,
      BrokerCodeComponent,

      OrderReferencePatternComponent,
      SourceComponent,
      FeeComponent,
      SrcMappingFeeComponent,
      SrcMappingTradeTypeComponent,
      SrcMappingExchangeComponent,
      SrcMappingAccountCodeComponent,
      SrcMappingBrokerCodeComponent,
      SrcMappingTraderComponent,
      SrcMappingContractComponent,
      MappingSidebarComponent,

      ArchiveSidebarComponent,
      RecapSidebarComponent,
      BillingSidebarComponent,
      BillingListComponent,
      UpdateOutstandingFormComponent,
      InvoiceListTableComponent,
      InvoiceDetailTableComponent,
      InvoiceDetailDialogComponent,
      ArchiveFormAbsComponent,
      ReportFormComponent,
      RecapFormComponent,
      ProfileAbsComponent,
      BatchUpdateProfileFormComponent,
      ReportProfileComponent,
      RecapProfileComponent,
      RecapListComponent,
      RecapListDialogComponent,
      RecapTasksComponent,
      InvoiceTasksComponent,
      ReportTasksComponent,
      ReportTasksTableComponent,
      ReportTasksButtonComponent,
      ReportTotalResidualFormComponent,
      ReportTotalResidualAdjustButtonComponent,
      UserComponent,
      BatchUpdateUserFormComponent,
      BatchUpdateOrderAmendmentFormComponent,
      UserGroupComponent,
      UserFormComponent,
      UserViewComponent,
      UserViewFormComponent,
      UserViewDialogComponent,
      SchedulerViewComponent,
      NotifyComponent,
      SupportSidebarComponent,
      SyncComponent,
      TradeCrudComponent,
      BatchActionSupportTradeFormComponent,
      OrderCrudComponent,
      BatchActionSupportOrderFormComponent,
      ClearingCrudComponent,
      BatchActionSupportClearingFormComponent,
      HealthComponent,
      NewETicketComponent,
      ListETicketComponent,
      ETicketViewsComponent,
      OrderAmendmentDialogComponent,
      OrderAmendmentTableComponent,
      NewETicketComponent,
      ListETicketComponent,
      ETicketViewsComponent,
      HogoButtonComponent,
      // crystal-element
      CrudBaseComponent,
      CcFormComponent,
      CcFormDialogComponent,
      CcTableComponent,
      CcTableEditComponent,
      CcCellDispatcherComponent,
      CcTreeTableComponent,
      CrudTableComponent,
      LanguageComponent,
      InputRegularComponent,
      ButtonComponent,
      CheckboxComponent,
      DialogComponent,
      RadioFieldComponent,
      ValidationComponent,
      LogoComponent,
      LightLogoComponent,
      CustomerLogoComponent,
      RichFieldComponent,
      SingleRichFieldComponent,
      MapFieldComponent,
      ReadonlyMapFieldComponent,
      ReadonlyRichFieldComponent,
      CronFieldComponent,
      CcVscrollTableComponent,
      CcVscrollTableBaseComponent,
      CctableBaseComponent,
      SafeHtmlPipe,
      StyleStringToObjPipe,
      SummaryChartComponent,
      ChartExchangeButtonsComponent,
      DataTrendChartComponent,
      ClearedUnclearedChartComponent,
      AddDeleteFieldComponent,
      TableFormFieldComponent,
      CcFieldDispatcherComponent,
      CcSubFormBaseComponent,
      CcSubDetailBaseComponent,
      PlResidualTableComponent,
      PlResidualTableButtonComponent,
      TradeSelectionTableComponent,
      TradeSelectionTableDialogComponent,
      HelpComponent,
      SelectionSummaryComponent,
      FindOptionTextPipe,
      DateFormatPipe,
      ParallaxBackgroundComponent,
      LoginFormComponent,
      LoginCredentialsComponent,
      ForgotPasswordComponent,
      ChangePasswordComponent,
      ResetPasswordComponent,
      ExpiredTokenComponent,
      LoginComponent,
    ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    HttpClientModule,
    NgxEchartsModule.forRoot({
      echarts: () => import('echarts')
    }),
    ChartModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    FormsModule,
    ReactiveFormsModule,
    ExportAsModule,
    QuartzCronModule,
    // primeng
    MenuModule,
    SlideMenuModule,
    TieredMenuModule,
    MenubarModule,
    ToastModule,
    SidebarModule,
    TooltipModule,
    DropdownModule,
    MultiSelectModule,
    ListboxModule,
    AutoCompleteModule,
    DialogModule,
    CheckboxModule,
    ToggleButtonModule,
    InputSwitchModule,
    InputTextModule,
    InputNumberModule,
    PasswordModule,
    ContextMenuModule,
    RadioButtonModule,
    SelectButtonModule,
    MessagesModule,
    MessageModule,
    ButtonModule,
    ToolbarModule,
    ConfirmDialogModule,
    ConfirmPopupModule,
    InputTextareaModule,
    TabViewModule,
    ProgressBarModule,
    CardModule,
    FieldsetModule,
    DividerModule,
    ProgressSpinnerModule,
    SplitterModule,
    PanelModule,
    TabMenuModule,
    TreeTableModule,
    ChipsModule,
    CalendarModule,
    OverlayPanelModule,
    BadgeModule,
    EditorModule,
    VirtualScrollerModule,
    SkeletonModule,
    // material
    MatTabsModule,
    MatIconModule,
    //Dashboard
    DockModule,
    RippleModule,
    NgxEchartsModule,
    MatGridListModule,
    MatCardModule,
    MatMenuModule,
    MatButtonModule,
    LayoutModule,
    ScrollingModule,
    TableModule,
    ClipboardModule,
    SpinnerModule,
    OrderListModule,
    PickListModule,
    FileUploadModule,
    ForceFocusDirective,
    TranslateModule
  ],
    providers: [
      {
        provide: APP_INITIALIZER,
        useFactory: appConfigInit,
        multi: true,
        deps: [AppConfigService]
      },
      {
        provide: APP_INITIALIZER,
        useFactory: ApplicationInitializerFactory,
        deps: [TranslateService, Injector],
        multi: true
      },
      {
        provide: APP_INITIALIZER,
        multi: true,
        deps: [LastActiveService],
        useFactory: (lastActiveService: LastActiveService) => () =>
          lastActiveService.setUp(),
      },
      {
        provide: APP_INITIALIZER,
        multi: true,
        deps: [UserInfoStorageService],
        useFactory: (userInfoStorageService: UserInfoStorageService) => () =>
          userInfoStorageService.setUp(),
      },
      ConfigServiceProvider,
      {
        provide: HTTP_INTERCEPTORS,
        useClass: TokenInterceptor,
        multi: true
      },
      {
        provide: HTTP_INTERCEPTORS,
        useClass: HttpErrorInterceptor,
        multi: true
      },
      {
        provide: RouteReuseStrategy,
        useClass: CustomRouteReuseStrategy
      },
      {
        provide: 'externalUrlRedirectResolver',
        useValue: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
          window.location.href = (route.data as any).externalUrl;
        }
      },
      IsNotLoggedPageGuard,
      IsLoggedPageGuard,
      MessageService,
      ConfirmationService,
      CookieService,
      DatePipe,
      DecimalPipe,
      {provide: MediaMatcher, useClass: CspMediaMatcher},
    ],
    bootstrap: [AppComponent],
    exports: [
      InputRegularComponent,
      ButtonComponent
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
  }
)
export class AppModule {
}

export function HttpLoaderFactory(httpClient: HttpClient) {
  // return new TranslateHttpLoader(httpClient, './assets/i18n/', '.json');
  return new TranslateHttpLoader(httpClient);
}

export function appConfigInit(appConfigService: AppConfigService) {
  return () => {
    return appConfigService.load()
  };
}
